import React from 'react'
import { Link } from 'react-router-dom'

const TermsOfUsePage = () => {
  return (
    <main className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <header className="mb-10 sm:mb-14">
          <p className="text-sm font-semibold text-emerald-600 mb-2 uppercase tracking-wide">
            Terms of Use
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-900 mb-4 leading-tight">
            The rules for using the Obule platform
          </h1>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl font-light">
            These Terms of Use (&quot;Terms&quot;) govern your access to and use of the Obule
            website, dashboards, mobile experiences, APIs, and related services (collectively, the
            &quot;Services&quot;). By creating an account, purchasing tickets or offers, or
            otherwise using Obule, you agree to be bound by these Terms. If you do not agree, you
            must not use the Services.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </header>

        <div className="space-y-10">
          {/* 1. Acceptance */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              1. Acceptance of these Terms
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              By accessing or using the Services in any manner, including but not limited to
              visiting or browsing the site, creating an account, purchasing tickets or offers,
              creating or managing events, or interacting with dashboards, you acknowledge that you
              have read, understood, and agree to be bound by these Terms and our{' '}
              <Link to="/privacy" className="text-emerald-600 hover:text-emerald-700 underline">
                Privacy Policy
              </Link>
              . If you are using the Services on behalf of an organization, you represent and
              warrant that you have the authority to bind that organization to these Terms.
            </p>
          </section>

          {/* 2. Eligibility & accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              2. Eligibility and account registration
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              You may use Obule only if you are at least the age of majority in your jurisdiction
              (typically 18 years or older) and are capable of forming a binding contract. To use
              certain features, you must create an account and provide accurate, current, and
              complete information. You agree to update your information as needed to keep it
              accurate and complete.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              You are responsible for safeguarding your account credentials and for all activities
              that occur under your account. You agree to notify us promptly if you suspect any
              unauthorized use of your account or any other security breach. We are not liable for
              any loss or damage arising from your failure to protect your credentials.
            </p>
          </section>

          {/* 3. Use of the Services */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              3. Use of the Services
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Obule provides tools for attendees to discover and purchase event tickets and offers,
              and for centers/organizers to create, manage, and analyze events and offers. You
              agree to use the Services only for lawful purposes and in accordance with these
              Terms, any additional guidelines we publish, and all applicable laws and regulations.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>Attendees may browse, purchase, and manage tickets and offers for personal use.</li>
              <li>
                Centers and organizers may use the dashboard to manage events, offers, attendees,
                and related data.
              </li>
              <li>
                Admin and support users may access additional functionality solely for internal
                operational, security, and compliance purposes.
              </li>
            </ul>
          </section>

          {/* 4. Purchases, tickets, and offers */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              4. Purchases, tickets, and offers
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              When you purchase a ticket or accept an offer through Obule, you are entering into a
              direct relationship with the relevant center or organizer. Unless explicitly stated
              otherwise, Obule acts as a technology platform and not as the seller or organizer of
              the event. The organizer is responsible for the event, the quality and availability
              of the services they provide, and any post‑purchase support.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              Tickets and offers are typically non‑transferable and non‑refundable except as
              permitted by the organizer&apos;s policies or by applicable law. Before completing a
              purchase, you are responsible for reviewing all relevant terms posted by the
              organizer, including age restrictions, entry requirements, and refund or cancellation
              rules.
            </p>
          </section>

          {/* 5. Payments and refunds */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              5. Payments, fees, and refunds
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Payments made through Obule are processed by our third‑party payment provider. By
              submitting a payment, you authorize our provider to charge your selected payment
              method for the full amount of the order, including any applicable taxes, fees, or
              service charges. You represent and warrant that you are authorized to use the chosen
              payment method.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              Refunds, if available, are governed by the organizer&apos;s policy and by our{' '}
              <Link to="/refund" className="text-emerald-600 hover:text-emerald-700 underline">
                Refund Policy
              </Link>
              . We may, at our discretion, facilitate certain refunds or credits, but we are not
              obligated to do so unless required by law or explicitly stated. Chargebacks or
              payment disputes may result in suspension or termination of your account.
            </p>
          </section>

          {/* 6. Prohibited activities */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              6. Prohibited activities
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              You agree not to misuse the Services or help anyone else to do so. Prohibited
              activities include, but are not limited to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-slate-600 font-light">
              <li>Engaging in any unlawful, fraudulent, or deceptive activity.</li>
              <li>
                Circumventing or attempting to circumvent ticketing, pricing, or availability
                controls, including by creating multiple accounts or using automated tools to hoard
                inventory.
              </li>
              <li>
                Interfering with or disrupting the integrity or performance of the Services, or
                attempting to probe, scan, or test the vulnerability of our systems.
              </li>
              <li>
                Scraping, crawling, or using automated means to access the Services without our
                prior written consent.
              </li>
              <li>
                Uploading malicious code, or content that infringes third‑party rights, is
                defamatory, hateful, or otherwise objectionable.
              </li>
              <li>
                Impersonating any person or entity, or falsely stating or misrepresenting your
                affiliation with any person or entity.
              </li>
            </ul>
          </section>

          {/* 7. Intellectual property */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              7. Intellectual property
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              The Services, including the Obule name and logos, product and feature names,
              dashboards, designs, text, graphics, and other content, are owned by or licensed to
              Obule and are protected by copyright, trademark, and other laws. Except as expressly
              authorized by us in writing, you may not copy, modify, distribute, sell, or lease any
              part of the Services or the underlying software.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              By submitting content (such as event descriptions, images, or logos) to Obule, you
              grant us a non‑exclusive, worldwide, royalty‑free license to use, reproduce, modify,
              adapt, publish, and display such content solely for the purpose of operating and
              promoting the Services. You represent and warrant that you have the necessary rights
              to grant this license.
            </p>
          </section>

          {/* 8. Disclaimers & limitation of liability */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              8. Disclaimers and limitation of liability
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              The Services are provided on an &quot;as is&quot; and &quot;as available&quot; basis
              without warranties of any kind, whether express or implied. To the fullest extent
              permitted by law, Obule disclaims all warranties, including implied warranties of
              merchantability, fitness for a particular purpose, and non‑infringement. We do not
              guarantee that the Services will be uninterrupted, secure, error‑free, or that
              defects will be corrected.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              To the maximum extent permitted by law, Obule and its affiliates, officers,
              employees, and agents will not be liable for any indirect, incidental, special,
              consequential, or punitive damages, or any loss of profits or revenues, whether
              incurred directly or indirectly, or any loss of data, use, goodwill, or other
              intangible losses, resulting from your use of or inability to use the Services. Our
              total liability for any claims relating to the Services will not exceed the greater
              of (a) the amount you paid us in the 6 months preceding the claim, or (b) a nominal
              amount where no payment was made.
            </p>
          </section>

          {/* 9. Indemnity */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. Indemnification</h2>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              You agree to indemnify, defend, and hold harmless Obule and its affiliates, officers,
              directors, employees, and agents from and against any and all claims, liabilities,
              damages, losses, and expenses (including reasonable attorneys&apos; fees) arising out
              of or in any way connected with your access to or use of the Services, your content,
              your violation of these Terms, or your violation of any rights of another person or
              entity.
            </p>
          </section>

          {/* 10. Governing law & changes */}
          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">
              10. Governing law and changes to these Terms
            </h2>
            <p className="text-base text-slate-600 leading-relaxed font-light mb-3">
              These Terms and any dispute arising out of or relating to them or the Services will be
              governed by and construed in accordance with the laws of the jurisdiction in which
              Obule is primarily established, without regard to its conflict of law provisions. You
              and Obule agree to submit to the exclusive jurisdiction of the courts located in that
              jurisdiction, except where applicable law provides otherwise.
            </p>
            <p className="text-base text-slate-600 leading-relaxed font-light">
              We may modify these Terms from time to time to reflect changes in our Services,
              practices, or legal requirements. When we make material changes, we will provide
              notice through the Services, by email, or by other reasonable means, and update the
              &quot;Last updated&quot; date above. Your continued use of the Services after such
              changes become effective constitutes your acceptance of the updated Terms.
            </p>
          </section>
        </div>
      </div>
    </main>
  )
}

export default TermsOfUsePage

